/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef, useMemo } from "react";
import { callOperation } from "../../../utils/apiHelper.js";
import { variantService } from "../services/variantService.js";
import VariantIdRenderer from "../../renderers/VariantIdRenderer";

export const useVariantGrid = (token, columnGroupVisibility = {}) => {
  const [columnDefs, setColumnDefs] = useState([]);
  const [filter_results, setFilterResult] = useState([]);
  const [group_counts, setGroupCounts] = useState({});
  const gridRef = useRef();
  const tokenRef = useRef(token);

  // Update token ref when token changes
  tokenRef.current = token;

  const getFilterConfig = (cellDataType, isVariantInfo) => {
    if (!isVariantInfo) return { filter: false };

    return {
      filter:
        cellDataType === "number"
          ? "agNumberColumnFilter"
          : "agTextColumnFilter",
      filterParams:
        cellDataType === "number"
          ? {
              filterOptions: [
                "equals",
                "greaterThan",
                "lessThan",
                "greaterThanOrEqual",
                "lessThanOrEqual",
              ],
            }
          : { filterOptions: ["equals"] },
    };
  };

  const defaultColDef = useMemo(() => {
    return {
      minWidth: 120, // Prevent columns from becoming too narrow
      flex: 1, // Use flex for responsive behavior
      sortable: false,
      menuTabs: ["columnsMenuTab"], // Only show columns visibility tab
      cellStyle: { textAlign: "center" },
      headerClass: "ag-header-center",
    };
  }, []);

  const createServerSideDatasource = useCallback(() => {
    const fetchGridData = (startRow, endRow, sortModel, filterModel) =>
      callOperation(
        "get_grid_data",
        {
          startRow,
          endRow,
          sortModel,
          filterModel,
        },
        tokenRef.current
      );

    return {
      getRows: async (params) => {
        try {
          const { startRow, endRow, sortModel, filterModel } = params.request;
          const response = await fetchGridData(
            startRow,
            endRow,
            sortModel,
            filterModel
          );

          // Fix backend returning invalid JSON with NaN values
          const result = typeof response === 'string'
            ? JSON.parse(response.replace(/:\s*NaN\s*([,}])/g, ': null$1')).data
            : response.data;

          const rows = result.rows ?? [];
          const requested = endRow - startRow;

          let rowCount = result.lastRow;
          if (rows.length < requested) {
            rowCount = startRow + rows.length;
          }

          params.success({ rowData: rows, rowCount });

          // Background prefetch next page
          const nextStart = endRow;
          const nextEnd = nextStart + requested;
          if (nextStart < rowCount) {
            fetchGridData(nextStart, nextEnd, sortModel, filterModel).catch(
              () => {}
            );
          }

          setFilterResult(result.filter_results || []);
          setGroupCounts(result.group_counts || {});
        } catch (error) {
          console.error("Error fetching grid data:", error);
          params.fail();
        }
      },
    };
  }, []); // Empty deps - stable reference

  const onColumnGroupOpened = useCallback((params) => {
    params.api.autoSizeAllColumns();
  }, []);

  const onAdvancedFilterBuilderVisibleChanged = useCallback((event) => {
    const div = document.getElementById("advancedFilterBuilderButton");
    if (!div) return; // Element doesn't exist in this context

    if (event.visible) {
      div.setAttribute("disabled", "");
    } else {
      div.removeAttribute("disabled");
    }
  }, []);

  const showBuilder = useCallback(() => {
    if (!gridRef.current?.api) {
      return;
    }
    gridRef.current.api.showAdvancedFilterBuilder();
  }, []);

  const refreshCache = useCallback(async () => {
    if (!gridRef.current?.api) {
      return;
    }
    gridRef.current.api.refreshServerSide({ route: [], purge: true });
  }, []);

  const onGridReady = useCallback(
    async (params) => {
      // Initialize grid data first
      try {
        const response = await variantService.getHeaders(tokenRef.current);
        const result = response.data;

        // Set up columns with the initialization callback
        if (window.initializeGridCallback) {
          window.initializeGridCallback(result);
        }
      } catch (error) {
        console.error("Error initializing grid:", error);
      }

      // Then create and set datasource
      const datasource = createServerSideDatasource();
      params.api.setGridOption("serverSideDatasource", datasource);
      params.api.setGridOption(
        "advancedFilterParent",
        document.getElementById("advancedFilterParent")
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createServerSideDatasource]
  );

  const setColumnFromData = useCallback((result, setRowIndex) => {
    let cols = [];
    for (let i = 0; i < result.length; i++) {
      const groupName = result[i]["headerName"];
      const isVisible = columnGroupVisibility[groupName] !== false; // Default to visible

      cols.push({
        headerName: groupName,
        children: [],
        suppressStickyLabel: true,
      });

      for (let j = 0; j < result[i]["children"].length; j++) {
        const columnData = result[i]["children"][j];

        const isVariantInfo = result[i]["headerName"] === "Variant Info";
        const isVariantSite = result[i]["headerName"] === "Variant Site";
        const filterConfig = getFilterConfig(
          columnData["cellDataType"],
          isVariantInfo || isVariantSite
        );

        const baseColDef = {
          headerName: columnData["headerName"],
          field: columnData["field"],
          cellDataType: columnData["cellDataType"],
          hide: !isVisible,
          ...filterConfig,
        };

        // First column is always visible, others only when group is expanded
        if (j > 0) {
          baseColDef.columnGroupShow = "open";
        }

        cols[i]["children"].push(baseColDef);

        // if (result["cols"][i]["children"][j]["headerName"] === "Gene Symbol") {
        //   cols[i]["children"][j]["tooltipField"] = "SYMBOL";
        //   cols[i]["children"][j]["tooltipValueGetter"] = function (params) {
        //     return params.data;
        //   };
        // }
        if (result[i]["children"][j]["headerName"] === "ID") {
          cols[i]["children"][j]["cellRenderer"] = VariantIdRenderer;
        }
      }
    }

    setColumnDefs(cols);
  }, [columnGroupVisibility]);

  const setFilterData = (filterResults, groupCounts) => {
    setFilterResult(filterResults);
    setGroupCounts(groupCounts || {});
  };

  const toggleColumnGroup = useCallback((groupName, isVisible) => {
    if (!gridRef.current?.api) {
      return;
    }

    // Update column definitions to show/hide the specified column group
    setColumnDefs((prevCols) =>
      prevCols.map((colGroup) =>
        colGroup.headerName === groupName
          ? {
              ...colGroup,
              children: colGroup.children.map((child) => ({
                ...child,
                hide: !isVisible,
              })),
            }
          : colGroup
      )
    );
  }, []);

  return {
    columnDefs,
    filter_results,
    group_counts,
    gridRef,
    defaultColDef,
    createServerSideDatasource,
    onColumnGroupOpened,
    onAdvancedFilterBuilderVisibleChanged,
    showBuilder,
    refreshCache,
    onGridReady,
    setColumnFromData,
    setFilterResult: setFilterData, // Updated to handle both filter_results and group_counts
    toggleColumnGroup,
  };
};
